import React, { useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import { useFeatures } from '../common/FeatureContext'
import { ClientFormWrapper } from '../common/styledComponents'
import axiosClient, { SERVER_URL } from '../service/axiosClient'

interface ConnectionStatus {
    connected: boolean
    message: string
}

interface SocialConnectionStatus {
    connections: Record<string, ConnectionStatus>
}

interface SocialNetworkConfig {
    key: string
    label: string
    logo: React.ReactNode
    connectAction: string
}

const FacebookLogo = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
)

const InstagramLogo = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
        <defs>
            <radialGradient id="instagram-gradient" cx="30%" cy="107%" r="150%">
                <stop offset="0%" stopColor="#fdf497"/>
                <stop offset="5%" stopColor="#fdf497"/>
                <stop offset="45%" stopColor="#fd5949"/>
                <stop offset="60%" stopColor="#d6249f"/>
                <stop offset="90%" stopColor="#285AEB"/>
            </radialGradient>
        </defs>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
)

const LinkedInLogo = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#0A66C2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
)

const TikTokLogo = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#000000">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
)

const GoogleLogo = () => (
    <svg width="32" height="32" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
)

const translateStatusMessage = (message: string | undefined, t: (k: string) => string): string => {
    if (!message) return ''
    const map: Record<string, string> = {
        'Pripojené': 'social_connected',
        'Nepripojené': 'social_not_connected',
        'Token expiroval': 'social_token_expired',
    }
    return map[message] ? t(map[message]) : message
}

const SOCIAL_NETWORKS: SocialNetworkConfig[] = [
    {key: 'FACEBOOK', label: 'Facebook', logo: <FacebookLogo/>, connectAction: 'meta/facebook/login'},
    // Instagram signs in on instagram.com through the Instagram Login setup of the Meta app,
    // so the user never sees a Facebook dialog. The Facebook Login route
    // (meta/instagram/login) stays available as a fallback if this one stops working.
    {key: 'INSTAGRAM', label: 'Instagram', logo: <InstagramLogo/>, connectAction: 'meta/instagram-direct/login'},
    {key: 'LINKED_IN', label: 'LinkedIn', logo: <LinkedInLogo/>, connectAction: 'linkedin/login'},
    {key: 'TIKTOK', label: 'TikTok', logo: <TikTokLogo/>, connectAction: 'tiktok/login'},
    {key: 'GOOGLE_CALENDAR', label: 'Google Calendar', logo: <GoogleLogo/>, connectAction: 'google/login'},
]

export const SocialCredentials = () => {
    const {t} = useTranslation()
    const {isFeatureEnabled} = useFeatures()
    const metaEnabled = isFeatureEnabled('meta_integration')

    const [statuses, setStatuses] = useState<Record<string, ConnectionStatus>>({})
    const [loading, setLoading] = useState(true)

    function loadStatuses() {
        axiosClient.get<SocialConnectionStatus>('/user/social-status').then(response => {
            setStatuses(response.data.connections || {})
        }).catch(error => {
            console.error('Failed to load social connection status', error)
        }).finally(() => {
            setLoading(false)
        })
    }

    useEffect(() => {
        loadStatuses()
    }, [])

    function connectToSocialNetwork(connectAction: string) {
        const url = `${SERVER_URL}/${connectAction}`
        axiosClient.get(url).then(response => {
            window.location.href = response.data
        }).catch(error => {
            console.error('error', error)
            toast.error(t('social_redirect_error'))
        })
    }

    function disconnectSocialNetwork(networkKey: string) {
        axiosClient.delete(`/user/social/${networkKey}`).then(() => {
            const disconnected = {connected: false, message: ''}
            setStatuses(prev => {
                const updated = {...prev, [networkKey]: disconnected}
                if (networkKey === 'FACEBOOK' || networkKey === 'INSTAGRAM') {
                    updated['FACEBOOK'] = disconnected
                    updated['INSTAGRAM'] = disconnected
                }
                return updated
            })
            loadStatuses()
        }).catch(error => {
            console.error('error', error)
            toast.error(t('social_disconnect_error'))
        })
    }

    const networks = SOCIAL_NETWORKS.filter(
        n => metaEnabled || (n.key !== 'FACEBOOK' && n.key !== 'INSTAGRAM')
    )

    return (
        <ClientFormWrapper>
            {loading ? (
                <div className="text-center py-4">{t('loading')}</div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {networks.map((network) => {
                        const status = statuses[network.key]
                        const isConnected = status?.connected || false

                        return (
                            <div key={network.key}
                                 className="d-flex align-items-center justify-content-between p-3 border rounded"
                                 style={{backgroundColor: '#fafafa'}}>
                                <div className="d-flex align-items-center gap-3">
                                    <div style={{width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                        {network.logo}
                                    </div>
                                    <div>
                                        <h5 className="mb-0">{network.label}</h5>
                                        <small className={isConnected ? 'text-success' : 'text-muted'}>
                                            {translateStatusMessage(status?.message, t) || t('social_not_connected')}
                                        </small>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    {isConnected && (
                                        <button
                                            onClick={() => disconnectSocialNetwork(network.key)}
                                            type="button"
                                            className="btn btn-sm btn-outline-danger btn-amani"
                                            style={{whiteSpace: 'nowrap', width: 'auto', display: 'inline-block'}}>
                                            {t('social_disconnect')}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => connectToSocialNetwork(network.connectAction)}
                                        type="button"
                                        className={`btn btn-sm ${isConnected ? 'btn-outline-success' : 'btn-danger'} btn-amani`}
                                        style={{whiteSpace: 'nowrap', width: 'auto', display: 'inline-block'}}>
                                        {isConnected ? t('social_reconnect') : t('social_connect_account')}
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </ClientFormWrapper>
    )
}
